import React, { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { LoadingType, RootState, setLoadingState } from 'actions';
import { setLoanPortal } from 'actions/dashboardPortal';
import { FundingInputName } from 'enums/FundingInputName';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getBorrowerByID } from 'thunks';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { BorrowerDto, BorrowerLockReason } from 'api/BorrowerApi';
import { AppRoutes } from 'routes/routes.enums';
import useAppNavigate from 'hooks/useAppNavigate';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { getErrorMessageForRequiredFields, removeSpaces } from 'utils/stringUtils';
import { FundingInputLabel } from 'enums/FundingInputLabel';
import {
  CHECKING_ACCOUNT_NUMBER_MAX_LENGTH,
  CHECKING_ACCOUNT_NUMBER_MIN_LENGTH,
  ROUTING_NUMBER_LENGTH,
} from 'utils/constants';
import NumberInput from 'components/Inputs/NumberInput';
import { FundingExample } from 'assets/images';
import { Button } from 'components/Buttons/Button';
import { updateRequiredInformation } from 'utils/updateRequiredInformation';
import { updateApplicationInStore } from 'utils/updateApplicationInStore';
import { bankAccountsApi } from 'api/BankAccountsApi';

import Section from '../Sections/Section';
import styles from './FundingInformation.module.scss';
import SubSection from '../Sections/SubSection';
import { SubSectionState } from '../Sections/SubSection/SubSection';

const FORM_ID = 'funding_form';

export interface FormDataBorrowerFundingInformation {
  [FundingInputName.BankAccountNumber]: string;
  [FundingInputName.ConfirmBankAccountNumber]: string;
  [FundingInputName.RoutingNumber]: string;
  [FundingInputName.ConfirmRoutingNumber]: string;
}

const FundingInfoForm = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();

  const { applicationId } = useSelector((state: RootState) => state.application);
  const { fundingInformationComplete, fundingInformationErrorMessage } = useSelector(
    (state: RootState) => state.dashboardPortal,
  );

  const borrower = useAppSelector((state) => state.borrower);

  const { register, trigger, handleSubmit, watch, formState, setValue, clearErrors, ...methods } =
    useForm<FormDataBorrowerFundingInformation>({
      shouldUnregister: false,
      reValidateMode: 'onChange',
    });

  const watcher = watch();

  useEffect(() => {
    register(FundingInputName.BankAccountNumber, {
      required: getErrorMessageForRequiredFields(FundingInputLabel.BankAccountNumber),
      validate: (value) => {
        return (
          (value.length >= CHECKING_ACCOUNT_NUMBER_MIN_LENGTH && value.length <= CHECKING_ACCOUNT_NUMBER_MAX_LENGTH) ||
          'Please enter a valid Bank Account Number'
        );
      },
    });
    register(FundingInputName.ConfirmBankAccountNumber, {
      required: getErrorMessageForRequiredFields(FundingInputLabel.ConfirmBankAccountNumber),
      validate: (value) => {
        return (
          !watcher[FundingInputName.BankAccountNumber] ||
          !watcher[FundingInputName.ConfirmBankAccountNumber] ||
          value === watcher[FundingInputName.BankAccountNumber].toString() ||
          'Entered number does not match Bank Account Number'
        );
      },
    });
    register(FundingInputName.RoutingNumber, {
      required: getErrorMessageForRequiredFields(FundingInputLabel.RoutingNumber),
      validate: (value) => {
        return value.length === ROUTING_NUMBER_LENGTH || 'Please enter a valid Routing Number';
      },
    });
    register(FundingInputName.ConfirmRoutingNumber, {
      required: getErrorMessageForRequiredFields(FundingInputLabel.ConfirmRoutingNumber),
      validate: (value) => {
        return (
          !watcher[FundingInputName.RoutingNumber] ||
          !watcher[FundingInputName.ConfirmRoutingNumber] ||
          value === watcher[FundingInputName.RoutingNumber].toString() ||
          'Entered number does not match Routing Number'
        );
      },
    });
  }, [clearErrors, register, watcher]);

  const handleBorrowerFraud = (borrowerDto: BorrowerDto) => {
    if (borrowerDto.variables.borrower_fraud) {
      if (borrowerDto.lockReason === BorrowerLockReason.BankFraud) {
        navigator(AppRoutes.NotQualified);

        return;
      }

      navigator(AppRoutes.Error);
    }
  };

  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  const onSubmit = handleSubmit(async (data) => {
    try {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: true,
        }),
      );

      const application = await bankAccountsApi.updateBankAccount({
        applicationId: applicationId!,
        bankAccountNumber: data[FundingInputName.BankAccountNumber],
        bankAccountRoutingNumber: data[FundingInputName.RoutingNumber],
      });

      const nextBankAccountStatus = application.variables.bank_accounts_refresh_status;

      if (application.status.name === ApplicationStatus.Rejected) {
        const borrowerDto = await dispatch(getBorrowerByID(borrower.borrowerId!)).unwrap();

        handleBorrowerFraud(borrowerDto);

        return;
      }

      updateApplicationInStore(application, dispatch);
      updateRequiredInformation(application, dispatch);

      dispatch(
        setLoanPortal({
          bankAccountsStatus: nextBankAccountStatus!,
        }),
      );
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: false,
        }),
      );
    }
  });

  const fundingSubSectionState: SubSectionState = {
    isCompleted: fundingInformationComplete,
    isExist: true,
  };

  return (
    <FormProvider
      register={register}
      trigger={trigger}
      handleSubmit={handleSubmit}
      formState={formState}
      watch={watch}
      setValue={setValue}
      clearErrors={clearErrors}
      {...methods}
    >
      <form onSubmit={onSubmit} id={FORM_ID}>
        <Section title={'Funding Information'} subSectionStates={[fundingSubSectionState]}>
          <SubSection state={fundingSubSectionState}>
            <p className={styles.label}>Your Bank Account is Used for Funding Your Loan Proceeds.</p>
            <p className={styles.info}>
              Your bank account will be used for funding your loan proceeds and for autopay, if you have elected for
              autopay.
            </p>
            <FundingExample className={styles.image} />
            <div className={styles.rowContainer}>
              <NumberInput
                label={FundingInputLabel.RoutingNumber}
                errorMessage={formState.errors[FundingInputName.RoutingNumber]?.message}
                className={styles.formInput}
                name={FundingInputName.RoutingNumber}
                onChange={(event: any) => {
                  clearErrors([FundingInputName.RoutingNumber, FundingInputName.ConfirmRoutingNumber]);
                  setValue(FundingInputName.RoutingNumber, event.target.value);

                  if (formState.isSubmitted) {
                    trigger(FundingInputName.RoutingNumber);
                  }
                }}
                onBlur={() => {
                  if (watcher[FundingInputName.ConfirmRoutingNumber]) {
                    trigger(FundingInputName.ConfirmRoutingNumber);
                  }

                  if (formState.isSubmitted) {
                    trigger(FundingInputName.RoutingNumber);
                  }
                }}
                onCopy={handleCopyPaste}
                maxLength={ROUTING_NUMBER_LENGTH}
                value={watcher[FundingInputName.RoutingNumber]}
                disabled={fundingInformationComplete}
              />
              <NumberInput
                label={FundingInputLabel.ConfirmRoutingNumber}
                errorMessage={formState.errors[FundingInputName.ConfirmRoutingNumber]?.message}
                className={styles.formInput}
                name={FundingInputName.ConfirmRoutingNumber}
                onChange={(event: any) => {
                  clearErrors([FundingInputName.RoutingNumber, FundingInputName.ConfirmRoutingNumber]);
                  setValue(FundingInputName.ConfirmRoutingNumber, event.target.value);

                  if (formState.isSubmitted) {
                    trigger(FundingInputName.ConfirmRoutingNumber);
                  }
                }}
                onBlur={() => {
                  clearErrors(FundingInputName.ConfirmRoutingNumber);

                  if (
                    formState.isSubmitted ||
                    (watcher[FundingInputName.RoutingNumber] && watcher[FundingInputName.ConfirmRoutingNumber])
                  ) {
                    trigger(FundingInputName.ConfirmRoutingNumber);
                  }
                }}
                onPaste={handleCopyPaste}
                value={watcher[FundingInputName.ConfirmRoutingNumber]}
                maxLength={ROUTING_NUMBER_LENGTH}
                autoComplete="new-password"
                disabled={fundingInformationComplete}
              />
            </div>
            <div className={styles.rowContainer}>
              <NumberInput
                label={FundingInputLabel.BankAccountNumber}
                errorMessage={formState.errors[FundingInputName.BankAccountNumber]?.message}
                className={styles.formInput}
                name={FundingInputName.BankAccountNumber}
                onChange={(event: any) => {
                  clearErrors([FundingInputName.BankAccountNumber, FundingInputName.ConfirmBankAccountNumber]);
                  setValue(FundingInputName.BankAccountNumber, removeSpaces(event.target.value));

                  if (formState.isSubmitted) {
                    trigger(FundingInputName.BankAccountNumber);
                  }
                }}
                onBlur={() => {
                  if (watcher[FundingInputName.ConfirmBankAccountNumber]) {
                    trigger(FundingInputName.ConfirmBankAccountNumber);
                  }
                }}
                onCopy={handleCopyPaste}
                value={watcher[FundingInputName.BankAccountNumber]}
                disabled={fundingInformationComplete}
              />
              <NumberInput
                label={FundingInputLabel.ConfirmBankAccountNumber}
                errorMessage={formState.errors[FundingInputName.ConfirmBankAccountNumber]?.message}
                className={styles.formInput}
                name={FundingInputName.ConfirmBankAccountNumber}
                onChange={(event: any) => {
                  clearErrors([FundingInputName.BankAccountNumber, FundingInputName.ConfirmBankAccountNumber]);
                  setValue(FundingInputName.ConfirmBankAccountNumber, removeSpaces(event.target.value));

                  if (formState.isSubmitted) {
                    trigger(FundingInputName.ConfirmBankAccountNumber);
                  }
                }}
                onBlur={() => {
                  clearErrors(FundingInputName.ConfirmBankAccountNumber);

                  if (
                    formState.isSubmitted ||
                    (watcher[FundingInputName.BankAccountNumber] && watcher[FundingInputName.ConfirmBankAccountNumber])
                  ) {
                    trigger(FundingInputName.ConfirmBankAccountNumber);
                  }
                }}
                onPaste={handleCopyPaste}
                value={watcher[FundingInputName.ConfirmBankAccountNumber]}
                autoComplete="new-password"
              />
            </div>
            {!!fundingInformationErrorMessage && (
              <div className={styles.errorContainer}>
                <div className={styles.errorLabel}>{fundingInformationErrorMessage}</div>
              </div>
            )}
            {!fundingSubSectionState.isCompleted && (
              <Button className={styles.submitButton} onClick={onSubmit}>
                Save
              </Button>
            )}
          </SubSection>
        </Section>
      </form>
    </FormProvider>
  );
};

export default FundingInfoForm;
